import { getSlideImageUrl } from 'ts/common/components/slideshows/slideshowImagePreloader';

/** How many opening slides a hover preview plays before looping. */
export const HOVER_PREVIEW_SLIDE_COUNT = 3;

/**
 * How long a hover will wait for those slides to be ready. Past this the tile stays on its
 * still — a preview that arrives late pops in after the pointer has already settled.
 */
export const HOVER_PREVIEW_START_DEADLINE_MS = 1500;

/**
 * A silent, looping excerpt of the slideshow's opening slides, keeping each slide's own
 * transition and Ken Burns. Song segments are dropped so the preview never plays audio or
 * ducks gallery music. Slide images are limited to the tile-sized renditions: the full
 * extra-large files are far larger than a grid tile and would miss the start deadline.
 * Returns null when none of those slides has an image to show.
 */
export function buildHoverPreviewSlideshow(
    slideshow: SpApi.IEventSlideshow
): SpApi.IEventSlideshow | null {
    const slides = (slideshow.slides ?? [])
        .slice(0, HOVER_PREVIEW_SLIDE_COUNT)
        .map(preferTileImage);

    if (!slides.some((slide) => getSlideImageUrl(slide))) {
        return null;
    }

    return {
        ...slideshow,
        slides,
        songSegments: [],
        slideshowLoop: true
    };
}

/**
 * Desktop hover preview for one slideshow tile. False on touch, under reduced motion, and
 * whenever another tile holds the single preview slot.
 */
export function isSlideshowHoverPreviewActive({
    allowsHoverPreview,
    hoveredPhotoId,
    photoId,
    photoType,
    prefersReducedMotion
}: {
    allowsHoverPreview: boolean;
    hoveredPhotoId: number | null;
    photoId: number;
    photoType: string | undefined;
    prefersReducedMotion: boolean;
}): boolean {
    return (
        photoType === 'slideshow' &&
        allowsHoverPreview &&
        !prefersReducedMotion &&
        hoveredPhotoId === photoId
    );
}

const DEFAULT_ASPECT = { width: 16, height: 9 };

/**
 * The slideshow frame, scaled up until it covers the tile and centered so the
 * tile clips the overflow. Letterboxing that frame would leave the cover still
 * showing around the playback.
 */
export function hoverPreviewCoverFrame(aspectRatio: string | null | undefined): {
    width: string;
    height: string;
} {
    const { width, height } = parseSlideshowAspectRatio(aspectRatio);

    return {
        width: `max(100cqw, calc(100cqh * ${width} / ${height}))`,
        height: `max(100cqh, calc(100cqw * ${height} / ${width}))`
    };
}

function parseSlideshowAspectRatio(aspectRatio: string | null | undefined): {
    width: number;
    height: number;
} {
    const [width, height] = (aspectRatio ?? '').split(':').map(Number);

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return DEFAULT_ASPECT;
    }

    return { width, height };
}

function preferTileImage(slide: SpApi.ISlideshowSlide): SpApi.ISlideshowSlide {
    return {
        ...slide,
        items: slide.items.map((item) => {
            if (!item.displayUrl) {
                return item;
            }

            return {
                ...item,
                displayUrl: {
                    medium: item.displayUrl.medium,
                    large: item.displayUrl.large
                }
            };
        })
    };
}
